import React from 'react';
import moment from 'moment-timezone';
import AutoIntl from '@au/core/lib/components/elements/AutoIntl';
import AuInput from '@au/core/lib/components/elements/AuInput';
import AuDropDown from '@au/core/lib/components/elements/AuDropDown';
import AuButton from '@au/core/lib/components/elements/AuButton';
import { ENTER_KEY } from '@au/core/lib/constants';

import { history as browserHistory } from "../../../history";
import { wrapActionWithTracking } from "../../../utils/analyticsHelpers";
import { formatMessage } from "../../../utils/reactIntl";
import { filtersToQueryParams } from '../../utils/filters';
import CollapsibleSearchList from "./CollapsibleSearchList";
import { OrDivider, SearchSummary } from "./SearchSectionParts";
import EmbeddedVehicleLookup from "./EmbeddedVehicleLookup";
import customStyles from "../../../css/components/vehicle_view.module.scss";
import searchStyles from "../../../css/components/search_section.module.scss";

const URL_KEY = 'propertyKey';
const URL_VALUE = 'propertyValue';
const SEARCH_SECTION_ID = 'vehicle-search-section';

export const PROPERTY_QUERY_PARAM = 'properties';
export const toPropertyParam = (key, value) => ({ [PROPERTY_QUERY_PARAM]: `${key}:${value}` });

export default class VehicleList extends CollapsibleSearchList {

  constructor(props) {
    super(props);

    const now = moment();
    const fixedCreateTimeStart = now.clone().subtract(29, 'days').toISOString();
    const fixedCreateTimeEnd = now.toISOString();

    const { key, value } = this.getPropertyFromUrl();
    const appliedProperty = key && value ? { key, value } : null;
    const propertyQueryParams = this.buildPropertyQueryParams(
        appliedProperty, fixedCreateTimeStart, fixedCreateTimeEnd
    );

    this.state = {
      ...this.state,
      propertyKey: key,
      propertyValue: value,
      appliedProperty,
      fixedCreateTimeStart,
      fixedCreateTimeEnd,
      propertyQueryParams,
      sidebarFilterQueryParams: {},
      filterQueryParams: propertyQueryParams,
      pendingCollapse: Boolean(appliedProperty)
    };
  }

  getPropertyFromUrl() {
    const params = new URLSearchParams(browserHistory.location.search);
    return {
      key: (params.get(URL_KEY) || '').trim(),
      value: (params.get(URL_VALUE) || '').trim()
    };
  }

  syncPropertyToUrl(property) {
    const params = new URLSearchParams(browserHistory.location.search);
    if (property) {
      params.set(URL_KEY, property.key);
      params.set(URL_VALUE, property.value);
    } else {
      params.delete(URL_KEY);
      params.delete(URL_VALUE);
    }
    browserHistory.replace({
      pathname: browserHistory.location.pathname,
      search: params.toString()
    });
  }

  buildPropertyQueryParams(property, start, end) {
    if (!property) return {};
    return {
      ...toPropertyParam(property.key, property.value),
      createTimeStart: start,
      createTimeEnd: end
    };
  }

  hasProperty() {
    return Boolean(this.state.appliedProperty);
  }

  async fetchInitialPages(pagesToLoad) {
    if (!this.hasProperty()) {
      this.setState({ fetching: false, allDataFetched: true });
      return Promise.resolve();
    }
    return super.fetchInitialPages(pagesToLoad);
  }

  getFilteredEntities() {
    if (!this.hasProperty()) {
      return this.props.entities.filter(() => false);
    }
    return super.getFilteredEntities();
  }

  getSupportedPropertyKeys() {
    const { entityDef } = this.props;
    return entityDef?.attributes?.properties?.display?.create?.props?.supportedProperties || [];
  }

  getPropertyKeyOptions() {
    const { propertyKey } = this.state;
    const keys = this.getSupportedPropertyKeys();
    const all = propertyKey && !keys.includes(propertyKey) ? [...keys, propertyKey] : keys;
    return all.map(k => ({ val: k, displayString: k }));
  }

  canApplyProperty() {
    const { propertyKey, propertyValue } = this.state;
    return Boolean((propertyKey || '').trim() && (propertyValue || '').trim());
  }

  onPropertyKeyChange = key => this.setState({ propertyKey: key || '' });
  onPropertyValueChange = e => this.setState({ propertyValue: e.target.value });

  onPropertyValueKeyDown = e => {
    if (e.key === ENTER_KEY) {
      this.applyProperty();
    }
  };

  applyProperty = () => {
    if (!this.canApplyProperty()) return;

    this.beginSearch();

    const key = this.state.propertyKey.trim();
    const value = this.state.propertyValue.trim();
    const { fixedCreateTimeStart, fixedCreateTimeEnd } = this.state;

    const next = { key, value };
    const propertyQueryParams = this.buildPropertyQueryParams(next, fixedCreateTimeStart, fixedCreateTimeEnd);

    this.setState(prev => ({
      appliedProperty: next,
      propertyQueryParams,
      filterQueryParams: { ...prev.sidebarFilterQueryParams, ...propertyQueryParams },
      pendingCollapse: true
    }), () => {
      this.syncPropertyToUrl(next);
      this.applyFilters();
    });
  };

  clearProperty = () => {
    this.beginSearch();

    this.setState(prev => ({
      appliedProperty: null,
      propertyKey: '',
      propertyValue: '',
      propertyQueryParams: {},
      filterQueryParams: { ...prev.sidebarFilterQueryParams },
      searchCollapsed: false,
      pendingCollapse: false
    }), () => {
      this.syncPropertyToUrl(null);
      this.applyFilters();
    });
  };

  initializeFilters(filters, filtersCount) {
    const sidebarFilterQueryParams = filtersToQueryParams(filters);
    this.setState({
      sidebarFilterQueryParams,
      filterQueryParams: { ...sidebarFilterQueryParams, ...this.state.propertyQueryParams },
      filtersCount
    }, () => {
      this.syncPropertyToUrl(this.state.appliedProperty);
      this._initFilters.resolve();
    });
  }

  handleFiltersChange(filters, filtersCount) {
    const sidebarFilterQueryParams = filtersToQueryParams(filters);
    this.setState({
      sidebarFilterQueryParams,
      filterQueryParams: { ...sidebarFilterQueryParams, ...this.state.propertyQueryParams },
      filtersCount
    }, () => {
      this.syncPropertyToUrl(this.state.appliedProperty);
      this.applyFilters();
    });
  }

  getUpdaterClassName() {
    return this.state.searchCollapsed
        ? customStyles.vehicle_updater_offset_collapsed
        : customStyles.vehicle_updater_offset;
  }

  renderPropertyInputs() {
    const { propertyKey, propertyValue } = this.state;
    return (
      <div className={customStyles.property_selector}>
        <label className={customStyles.property_label}>
          <AutoIntl displayId="au.entity.attr.property" />
        </label>
        <AuDropDown
          // AuDropDown keeps stale text when selection returns to empty; remount on set <-> empty
          key={propertyKey ? 'property_key_set' : 'property_key_empty'}
          className={customStyles.property_key_dropdown}
          options={this.getPropertyKeyOptions()}
          selection={propertyKey || undefined}
          selectOption={this.onPropertyKeyChange}
          placeholderId="au.vehicles.properties.namePlaceholder"
          />
        <AuInput
          type="text"
          name="propertyValue"
          className={customStyles.property_input}
          value={propertyValue}
          placeholderId="au.vehicles.properties.valuePlaceholder"
          onChange={this.onPropertyValueChange}
          onKeyDown={this.onPropertyValueKeyDown}
          onClear={() => this.onPropertyValueChange({ target: { value: '' } })}
          />
        <AuButton
          type="primary"
          displayId="au.vehicles.property.apply"
          className={customStyles.property_apply_btn}
          disabled={!this.canApplyProperty()}
          onClick={this.applyProperty}
          />
      </div>
    );
  }

  renderSearchSummary() {
    const { appliedProperty, searchCollapsed } = this.state;
    return (
      <SearchSummary
        collapsed={searchCollapsed}
        toggleName="vehicleSearchToggle"
        onToggle={this.onSearchToggleChange}
        onClear={this.clearProperty}
        clearLabel={formatMessage({ id: 'au.vehicles.property.clear' })}
        >
        <AutoIntl displayId="au.entity.attr.property" />: {appliedProperty.key} = {appliedProperty.value}
      </SearchSummary>
    );
  }

  renderContent() {
    const { appliedProperty, searchCollapsed } = this.state;
    return (
      <>
        {appliedProperty && this.renderSearchSummary()}
        <div
          id={SEARCH_SECTION_ID}
          className={searchCollapsed ? searchStyles.search_section_collapsed : undefined}
          >
          <EmbeddedVehicleLookup
            endpoint={this.props.endpoint}
            entityDef={this.props.entityDef}
            match={this.props.match}
            controlsClassName={customStyles.vehicle_lookup_controls}
            >
            <OrDivider />
            <AutoIntl
              displayId="au.vehicles.propertyBanner"
              tag="p"
              className={customStyles.vehicle_property_hint}
              />
            {this.renderPropertyInputs()}
          </EmbeddedVehicleLookup>
        </div>
        {super.renderContent()}
      </>
    );
  }

  getRowActions({ id, canWrite, permissions, ...otherProps }) {
    const actions = super.getRowActions({ id, canWrite, permissions, ...otherProps });

    const manageBindingAction = {
      key: 'manage_binding_btn',
      displayId: 'au.entity.manageBindings',
      onClick: () => browserHistory.push(this.baseUrl + `/${id}/manage-bindings`)
    };
    actions.splice(2, 0, wrapActionWithTracking(manageBindingAction, 'Vehicle', 'List'));

    return actions;
  }
}
